import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, input, OnInit, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AppFeatureService } from '@nk/access-block';
import { COMPANY_FLAG_API_SERVICE_TOKEN, CompanyFlagApiService } from '@nk/api-lib';
import { AppFeatureEntity, CompanyFlagTypeEntity, WithUuid } from '@nk/data-entities-lib';
import { ButtonDirective, ClickOutsideDirective } from '@nk/ui-lib';
import { arrayToDict, Dict } from '@nk/utils-lib';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { BehaviorSubject, combineLatest, map, Observable, switchMap } from 'rxjs';
import { sortCompanyFlag } from '../../utils/company-flag.utils';
import { CompanyFlagIconComponent } from '../company-flag-icon/company-flag-icon.component';

// TODO: Use CompanyBrowserFilterKey instead
enum CompanyBrowserCompanyFlagFilterType {
  Or = 'company_flag_history_aggregate_or',
  And = 'company_flag_history_aggregate_and',
}

interface CompanyFlagItem {
  companyFlagType: WithUuid<CompanyFlagTypeEntity>;
  requiredFeature: AppFeatureEntity | undefined;
  featureAccessGranted: boolean;
}

@Component({
  selector: 'nk-company-flag-select',
  imports: [
    AsyncPipe,
    TranslateModule,
    FormsModule,
    TooltipModule,
    PopoverModule,
    ButtonDirective,
    CompanyFlagIconComponent,
    ClickOutsideDirective,
  ],
  templateUrl: './company-flag-select.component.html',
  styleUrl: './company-flag-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyFlagSelectComponent implements OnInit {
  public title = input('');
  public operator = input<'IN' | 'NOT_IN'>('IN'); // TODO: use CompanyBrowserFilterOperator
  public selectedFilterType = input<CompanyBrowserCompanyFlagFilterType>(CompanyBrowserCompanyFlagFilterType.Or);
  /**
   * Optional dictionary storing company flag type UUIDs selected in a nearby sibling filter.
   * This is used when there are multiple flag-based filters on the page—typically one for inclusion
   * and one for exclusion of companies in the company browser results. If only one such filter exists,
   * this dictionary remains empty by default.
   */
  public siblingSelectedCompanyFlagTypeUuidDict = input<Dict<string>>({});
  public selectedCompanyFlagTypeUuidDict = input<Dict<string>>({});
  public selectCompanyFlagType = output<string>();
  public searchQuery = '';
  public searchTrigger$ = new BehaviorSubject<string>(this.searchQuery);
  public popOverOpen = false;
  public translationPrefix = 'company_flag';
  public companyFlagItems$!: Observable<CompanyFlagItem[]>;

  constructor(
    @Inject(COMPANY_FLAG_API_SERVICE_TOKEN)
    private _companyFlagService: CompanyFlagApiService,
    private _appFeatureService: AppFeatureService,
    private _translateService: TranslateService,
  ) {}

  public ngOnInit(): void {
    this.companyFlagItems$ = this.searchTrigger$.pipe(
      switchMap((searchQuery) =>
        combineLatest([
          this._companyFlagService.companyFlagTypeListCache.get(),
          this._appFeatureService.appFeatureListByMembershipCache.get(),
          this._companyFlagService.legalCompanyFlagList(),
        ]).pipe(
          map(([companyFlagTypes, appFeatures, legalCompanyFlagDict]) => {
            const appFeatureDict = arrayToDict(appFeatures, (i) => i.uuid as string);
            let items = sortCompanyFlag(companyFlagTypes.slice(), this._translateService).map((i) => {
              const item: CompanyFlagItem = {
                companyFlagType: i,
                requiredFeature: appFeatureDict[i.app_feature_uuid],
                featureAccessGranted: !!legalCompanyFlagDict[i.uuid as string],
              };
              return item;
            });
            if (searchQuery) {
              const queryParts = searchQuery
                .toLowerCase()
                .split(' ')
                .filter((q) => q.length)
                .map((word) => ' ' + word);
              items = items.filter((i) => {
                const label = this._translateService
                  .instant(this.translationPrefix + '.' + i.companyFlagType.company_flag_type_key)
                  .toLowerCase();
                return queryParts.some((queryPart) => label.includes(queryPart));
              });
            }
            return items.filter((a) => a.companyFlagType.app_hidden_flag === false);
          }),
        ),
      ),
    );
  }

  public onSearch(query: string): void {
    this.searchTrigger$.next(query);
  }

  public toggleCompanyFlagType(companyFlagType: WithUuid<CompanyFlagTypeEntity>): void {
    this.clearSearchQuery();
    this.selectCompanyFlagType.emit(companyFlagType.uuid as string);
  }

  public clearSearchQuery(): void {
    this.searchQuery = '';
    this.searchTrigger$.next(this.searchQuery);
  }

  public onClickSearch(): void {
    this.popOverOpen = true;
  }

  public closePopover(): void {
    this.popOverOpen = false;
  }
}
